import { Link } from "@inertiajs/react"
import { cn } from "@/lib/cn"
import { Card } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { fmtRelativeTime } from "@/lib/format"
import type { SkillVersionRow } from "@/types/skill"

type Props = {
  versions: SkillVersionRow[]
  // Parent Skill id — stable across versions; used for `/skills/:id/edit` nav.
  skillId: number
  // SkillVersion id of the selected row (matches `SkillVersionRow.id`).
  selectedVersionId: number
  // Emits the clicked SkillVersion id; parent maps id → URL nav.
  onSelect: (skillVersionId: number) => void
  skillKey: string
}

// Right-rail Versions table. Click a row to switch the page's selected
// version. Each row also surfaces a quick-link to the parent skill's
// Edit page (which auto-resolves to the current draft version).
export function VersionsTable({ versions, skillId, selectedVersionId, onSelect, skillKey }: Props) {
  return (
    <Card className="p-0">
      <header className="flex items-center justify-between border-b border-border px-4 py-3">
        <div>
          <h3 className="text-sm font-semibold">Versions</h3>
          <p className="text-xs text-app-muted">{versions.length} in lineage · {skillKey}</p>
        </div>
      </header>
      <div className="divide-y divide-border">
        {versions.map((v) => {
          const here = v.id === selectedVersionId
          return (
            <div
              key={v.id}
              className={cn(
                "grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 px-4 py-3 text-sm transition",
                here ? "bg-brand-redSoft/30" : "hover:bg-white/[0.03]",
              )}
            >
              <button
                type="button"
                onClick={() => onSelect(v.id)}
                className="font-semibold text-app-text"
              >
                v{v.version}
              </button>
              <div className="min-w-0">
                <Badge tone={v.status === "live" ? "gold" : v.status === "draft" ? "red" : "neutral"}>
                  {v.status}
                </Badge>
                <div className="mt-1 truncate text-xs text-app-muted">
                  {v.notes ?? "—"}
                </div>
                <div className="text-[10px] text-app-subtle">
                  {fmtRelativeTime(v.promotedAt ?? v.updatedAt)}
                  {here && " · viewing"}
                </div>
              </div>
              <Link
                href={`/skills/${skillId}/test?tab=design`}
                className="text-xs text-vip-goldSoft hover:underline"
              >
                Open
              </Link>
            </div>
          )
        })}
      </div>
    </Card>
  )
}
